/* eslint-disable no-unused-vars */
import {useEffect, useState, useCallback} from 'react';
import {useNavTree} from '../Providers/NavTreeProvider';
import MediaLibraryView from '../views/MediaLibraryView';
import Util from '../util/Util';
import CurrentPathUtil from '../util/CurrentPathUtil';
import {smartFetch} from '../util/smartFetch'; // Contains USE_MOCKS flag
import Analytics from '../util/Analytics';
import {getAccountId, getInternalUser} from '../util/ApiConfig';
import {useAssets, FOLDER_IDX, TAG_IDX} from '../Providers/AssetsProvider';

// Table column indices for the columns that can sort
const SORT_COLUMN_NAME = 0;
const SORT_COLUMN_TYPE = 1;
const SORT_COLUMN_WIDGET_SIZE = 2;
const SORT_COLUMN_SIZE = 3;
const ADD_ICON_INDEX = 1;
const MOVE_ICON_INDEX = 2;
const CLOSE_MOVING_INDEX = 3;
const ADD_FOLDER_ACTION = `icon-${ADD_ICON_INDEX}`;
const MOVE_FOLDER_ACTION = `icon-${MOVE_ICON_INDEX}`;
const CLOSE_MOVING_ACTION = `icon-${CLOSE_MOVING_INDEX}`;
const RENAME_FOLDER_ACTION = 'edit';
const DELETE_FOLDER_ACTION = 'trash';

function initializeFilterSelect(filters) {
    if (filters && filters.length && filters.length < 3 && typeof filters !== 'string') {
        return filters[0];
    }
    return 'all';
}

const assetFilters = Util.getFilters();
let filterSelect = initializeFilterSelect(assetFilters);
let searchQuery = '';
let internalSortBy = 'dateDes';
let dateFrom = null; // inclusive epoch-ms lower bound (local start of day) or null
let dateTo = null; // inclusive epoch-ms upper bound (local end of day) or null
let tagChanges = false; // Refresh account tag list on tabbing to Tag View after tags have been added or deleted

const currentPage = [0, 0]; // current page for folder and tag views

export default function MediaLibraryController() {
    const [tagSelected, setTagSelected] = useState(-1);
    const [tags, setTags] = useState([]);
    const [movementSrc, setMovementSrc] = useState(null);
    const [, setShowFocused] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showGrid, setShowGrid] = useState(true);
    const [focused, setFocused] = useState(null);
    const [assetTags, setAssetTags] = useState([]);
    const [locked, setLocked] = useState(false);
    const [isDraggingAsset, setDraggingAsset] = useState(false);
    const [previousEnabled, setPreviousEnabled] = useState(false);
    const [nextEnabled, setNextEnabled] = useState(false);
    // TODO: Tag view doesn't really have selections but old code did. Keeping for future Composer multi-selection work
    const [tagSelections, setTagSelections] = useState([]);
    const [folderSelections, setFolderSelections] = useState([]);
    const [contextError, setContextError] = useState(null);
    const [focusTo, setFocusTo] = useState(null);

    const {pageState, pageDispatch} = useAssets();
    const {typeIndex, assets, totalAssets, loading, error, page, pageSize, maxPage, maintenance} = pageState;
    const {navtree, dispatchNavTree} = useNavTree();

    console.log(maintenance); // eslint-disable-line no-console

    const getSelections = () => {
        if (typeIndex === FOLDER_IDX) {
            return folderSelections;
        }
        return tagSelections;
    };

    const setSelections = selections => {
        if (typeIndex === FOLDER_IDX) {
            setFolderSelections(selections);
        } else {
            setTagSelections(selections);
        }
    };

    const clearSelections = () => {
        setFolderSelections(folderSelections.map(() => false));
        setTagSelections(tagSelections.map(() => false));
    };

    const loadFolder = (pathParm, folderData) => {
        const path = pathParm.length && pathParm[0] === Util.ROOT_DIR ? pathParm : Util.rootedArrayPath(pathParm);
        CurrentPathUtil.saveCurrentPath(path);
        const node = Util.findNode(path, JSON.parse(JSON.stringify(folderData)));
        const p = node ? node.path : null;
        pageDispatch({type: 'SET_FOLDER_ID', folderId: p});
        clearSelections();
        return true;
    };

    const clearPages = () => {
        currentPage[FOLDER_IDX] = 0;
        currentPage[TAG_IDX] = 0;
    };

    const onSelectChange = event => {
        const {target} = event;
        const {selections} = target;

        if (selections.every(selection => !selection)) {
            setMovementSrc(null);
        }
        setSelections(selections);
    };

    const getTagFromIndex = idx => {
        return typeof tags[idx] === 'string' ? tags[idx] : tags[idx].name;
    };

    const loadTaggedMedia = idx => {
        const tag = getTagFromIndex(idx);
        setShowFocused(false);
        pageDispatch({type: 'SET_TAG', tag});
        clearSelections();
        clearPages();
        return true;
    };

    const loadAssets = () => {
        let status = false;
        if (typeIndex === TAG_IDX) {
            status = loadTaggedMedia(tagSelected);
        } else if (navtree.selectedNode) {
            status = loadFolder(navtree.selectedNode.split('>'), navtree?.folderData);
        }
        return status;
    };

    const setPage = pag => {
        pageDispatch({type: 'SET_PAGE', page: pag});
        clearSelections();
        return null;
    };

    const setPageSize = size => {
        pageDispatch({type: 'SET_PAGE_SIZE', pageSize: size});
        clearSelections();
        clearPages();
        return true;
    };

    const getInternalSortBy = () => {
        return internalSortBy;
    };

    const changeInternalSortBy = internalValue => {
        pageDispatch({type: 'SET_SORT_BY', sortBy: internalValue});
        clearSelections();
        clearPages();
        internalSortBy = internalValue || '';
        return internalSortBy;
    };

    const getDateRange = () => {
        return {dateFrom, dateTo};
    };

    // from/to: inclusive epoch-ms bounds (local start/end of day) or null; persists across folder/tag switches
    const setDateRange = (from, to) => {
        dateFrom = from ?? null;
        dateTo = to ?? null;
        pageDispatch({type: 'SET_DATE_RANGE', dateFrom, dateTo});
        clearSelections();
        clearPages();
        return {dateFrom, dateTo};
    };

    // Converts sort-on table column index into an internal sortBy value
    const onSortColumnClick = tableColumn => {
        const sortBy = getInternalSortBy();
        let result = null;
        switch (tableColumn) {
            case SORT_COLUMN_NAME:
                result = sortBy === 'nameAsc' ? 'nameDes' : 'nameAsc';
                break;
            case SORT_COLUMN_WIDGET_SIZE:
                result = sortBy === 'sizeAsc' ? 'sizeDes' : 'sizeAsc';
                break;
            case SORT_COLUMN_SIZE:
                result = sortBy === 'sizeAsc' ? 'sizeDes' : 'sizeAsc';
                break;
            case SORT_COLUMN_TYPE:
            default:
                result = 'type';
        }
        changeInternalSortBy(result);
        return result;
    };

    const getViewData = () => {
        return assets;
    };

    const loadAssetTags = async assetId => {
        const data = await smartFetch(`${Util.ROOT_PATH}/asset/${Util.safeURIencode(assetId)}/tags`);
        if (data) {
            const computeAssetTags = data.length ? data.map(entry => entry.tag) : [];
            setAssetTags(computeAssetTags);
            return true;
        }
        return false;
    };

    // index is optional and used primarily for stock images which don't have an id
    const changeShowFocused = useCallback(async (asset, index) => {
        if (asset) {
            setFocused(prevFocused => ({...prevFocused, ...asset, index}));
            setAssetTags([]);
            if (asset.id) {
                await loadAssetTags(asset.id);
            }
        } else {
            setFocused(null);
            setAssetTags([]);
        }
    }, []);

    const updateAssetLabel = async assetObject => {
        if (!assetObject || !assetObject.id || !assetObject.label || (focused && focused.label === assetObject.label)) {
            return null; // nothing changed
        }

        const assetId = assetObject.id;
        const label = assetObject.label.trim();
        const payload = JSON.stringify({assetId, label: encodeURIComponent(label)});
        let ok = false;

        const result = await smartFetch(`${Util.WRITE_ROOT_PATH}/assets/${assetId}/label`, 'PATCH', payload);
        ok = !result || result.status === '200' || (!result?.message && result.id);

        if (ok) {
            setFocused(prev => ({...prev, label})); // Update focused state incrementally
            return true;
        }
        Analytics.trackEvent('Network Timeout', 'Editing Asset Label');
        return false;
    };

    const updateAssetAlt = async assetObject => {
        // TODO: Add error messaging if alt text is removed because the backend wont allow empty or null string
        if (
            !assetObject ||
            !assetObject.id ||
            !assetObject.altText ||
            (focused && focused.altText === assetObject.altText)
        ) {
            return null; // nothing changed
        }

        const assetId = assetObject.id;
        const altText = assetObject.altText.trim();

        const altResult = await smartFetch(`${Util.WRITE_ROOT_PATH}/asset/${assetId}/alt/${altText}`, 'PUT');
        if (altResult && altResult.id) {
            setFocused(prev => ({...prev, altText}));
            return true;
        }
        if (altResult && altResult.message) {
            Analytics.trackEvent('Network Timeout', 'Editing Asset Alt Text');
        }

        return false;
    };

    const onEditAsset = async assetObject => {
        Analytics.trackEvent('Asset', 'Edit');
        const status = {
            label: false,
            alt: false
        };
        const labelResult = updateAssetLabel(assetObject)
            .then(result => {
                status.label = result;
            })
            .catch(err => {
                if (process.env.NODE_ENV !== 'test') {
                    console.warn('error label', err); // eslint-disable-line
                }
            });
        const altResult = updateAssetAlt(assetObject)
            .then(result => {
                status.alt = result;
            })
            .catch(err => {
                if (process.env.NODE_ENV !== 'test') {
                    console.warn('error alt', err); // eslint-disable-line
                }
            });

        // eslint-disable-next-line no-return-await
        return await Promise.all([labelResult, altResult]).then(() => status);
    };

    const onAction = event => {
        // Just in case, we only want nativeEvents of type Event, not MouseEvent
        if (!(event.nativeEvent instanceof MouseEvent) && !movementSrc) {
            const {isDetails, target, doubleClick} = event;
            const {value} = target;

            if (isDetails) {
                if (doubleClick) {
                    const message = Util.formatSelectEvent(target);
                    window.parent.postMessage(JSON.stringify(message), '*');
                }
            } else {
                if (typeof value === 'string') {
                    onSortColumnClick(parseInt(value.substring(4), 10)); // get rid of 'sort' text
                    return null;
                }
                const focuzed = getViewData() && {...getViewData()[value]};
                if (doubleClick) {
                    const message = Util.formatSelectEvent(focuzed);
                    window.parent.postMessage(JSON.stringify(message), '*'); // Called from an iFrame, postMessage deals with CORS issues (last param)
                } else {
                    changeShowFocused(focuzed, value);
                }
            }
            return value;
        }
        return null;
    };

    // Convert map of from {"tag-string1": count1, ...} to [{name: "tag-string1", count: count1}, ...] or ["tag-string1", ...]
    //
    const loadTagsList = async (accountId, ignoreCounts = true) => {
        const data = await smartFetch(`${Util.ROOT_PATH}/account/${Util.safeURIencode(accountId)}/tags`);
        if (data) {
            const tagz = [];
            Object.keys(data).forEach(tag => {
                if (ignoreCounts) {
                    tagz.push(tag);
                } else {
                    tagz.push({name: tag, count: data[tag]});
                }
            });
            tagz.sort((a, b) => {
                if (ignoreCounts) {
                    if (a < b) {
                        return -1;
                    }
                    if (a > b) {
                        return 1;
                    }
                    return 0;
                }
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });
            setTags(tagz);
            return true; // Successful
        }
        Analytics.trackEvent('Network Timeout', 'Loading Tags');
        return false; // Unsuccessful
    };

    const changeLocked = status => {
        if (status !== locked) {
            setLocked(status);
        }
        return status;
    };

    const loadTopLevelData = () => {
        return new Promise(resolve => {
            const onComplete = async updatedState => {
                const {folderData, selectedNode, error: error_, locked: isLocked} = updatedState;
                changeLocked(isLocked || false);
                if (!error_) {
                    await loadFolder(Util.rootedArrayPath(selectedNode), folderData); // TEST TO GET JS ERROR
                }
                resolve(!error_);
            };
            dispatchNavTree({type: 'INITIALIZE'}, onComplete);
        });
    };

    const onLocationChange = (paths_, move = false) => {
        return new Promise(resolve => {
            const onComplete = updatedState => {
                changeLocked(updatedState.locked || false);
                resolve(updatedState);
            };
            dispatchNavTree({type: 'CHANGE_LOCATION', paths: paths_, move}, onComplete);
        });
    };

    const handleDisclosureSelection = async paths => {
        return new Promise(resolve => {
            const onComplete = updatedState => {
                const {selectedNode} = updatedState;
                resolve(true);
            };
            dispatchNavTree({type: 'SELECT_DISCLOSURE', paths}, onComplete);
        });
    };

    const completeMoveFolderAction = async (srcPaths, dstPaths) => {
        return new Promise(resolve => {
            const onComplete = updatedState => {
                const {folderData, error: error_} = updatedState;
                if (movementSrc) {
                    setMovementSrc(null);
                }
                if (!error_) {
                    loadFolder(dstPaths, folderData);
                }
                resolve(!error_);
            };
            dispatchNavTree({type: 'MOVE_FOLDER', srcPaths, dstPaths}, onComplete);
        });
    };

    const onFolderAction = async event => {
        changeShowFocused(false);
        // Just in case, we only want nativeEvents of type Event, not MouseEvent
        if (event.nativeEvent.type === 'input') {
            const {value} = event.target;
            if (typeof value === 'string') {
                const paths = Util.rootedArrayPath(event.target.value);
                const action = paths.pop();
                const node = Util.findNode(paths, navtree?.folderData);
                if (node) {
                    switch (action) {
                        case 'disclosure':
                            return handleDisclosureSelection(paths);
                        case ADD_FOLDER_ACTION:
                        case RENAME_FOLDER_ACTION:
                        case DELETE_FOLDER_ACTION:
                            break;
                        case MOVE_FOLDER_ACTION:
                            Analytics.trackEvent('Folder', 'Move');
                            setMovementSrc(paths);
                            onLocationChange(paths, true);
                            break;
                        case CLOSE_MOVING_ACTION:
                            setMovementSrc(null);
                            onLocationChange(paths);
                            break;
                        default: {
                            // General Folder Selected action
                            if (movementSrc) {
                                // eslint-disable-next-line no-return-await
                                return await completeMoveFolderAction(movementSrc, paths);
                            }
                            setMovementSrc(null);
                            await onLocationChange(paths);
                            return loadFolder(paths, navtree?.folderData);
                        }
                    }
                }
            } else if (value.drop && value.on) {
                Analytics.trackEvent('Folder', 'Move');
                // Drag n drop
                // eslint-disable-next-line no-return-await
                return await completeMoveFolderAction(Util.rootedArrayPath(value.drop), Util.rootedArrayPath(value.on));
            }
        }
        return null;
    };

    const onCreateFolder = title => {
        const parentPaths = navtree.selectedNode.split('>');
        return new Promise(resolve => {
            const onComplete = updatedState => {
                const {folderData, error: error_} = updatedState;
                if (!error_) {
                    loadFolder(parentPaths, folderData);
                }
                resolve(!error_);
            };
            dispatchNavTree({type: 'CREATE_FOLDER', title}, onComplete);
        });
    };

    const onRenameSelectedFolder = async rename => {
        return new Promise(resolve => {
            const onComplete = updatedState => {
                const {error: error_} = updatedState;
                resolve(!error_);
            };
            dispatchNavTree({type: 'RENAME_FOLDER', rename}, onComplete);
        });
    };

    const getParentPaths = childPaths => {
        return childPaths.slice(0, childPaths.length - 1);
    };

    const onDeleteSelectedFolder = async () => {
        const parentPaths = getParentPaths(navtree?.selectedNode.split('>'));
        return new Promise(resolve => {
            const onComplete = updatedState => {
                const {folderData, error: error_} = updatedState;
                if (!error_) {
                    loadFolder(parentPaths, folderData);
                }
                resolve(!error_);
            };
            dispatchNavTree({type: 'DELETE_FOLDER'}, onComplete);
        });
    };

    const isShowingTags = () => {
        return typeIndex === FOLDER_IDX;
    };

    const getLocked = () => {
        return typeIndex === FOLDER_IDX && locked;
    };

    const isAdvertisingRoot = (givenNode = navtree?.selectedNode) => {
        if (givenNode) {
            const pathParm = givenNode.split('>');
            const path = pathParm.length && pathParm[0] === Util.ROOT_DIR ? pathParm : Util.rootedArrayPath(pathParm);
            const node = Util.findNode(path, JSON.parse(JSON.stringify(navtree?.folderData)));
            return node && node.root && node.root === 'advertising';
        }
        return false;
    };

    const onTabSelect = idx => {
        const refreshTagsNeeded = idx === TAG_IDX && tagChanges;
        if (idx !== typeIndex || refreshTagsNeeded) {
            pageDispatch({type: 'SET_TYPE_INDEX', typeIndex: idx, page: currentPage[idx]});
            clearSelections();
            if (refreshTagsNeeded) {
                loadTagsList(getAccountId());
            }
        }
    };

    const getAssetList = () => {
        const selections = getSelections();
        if (!selections || !assets.length) {
            return null;
        }
        const moveAssetList = [];
        selections.forEach((isSelected, idx) => {
            if (isSelected) {
                moveAssetList.push(assets[idx]);
            }
        });
        return moveAssetList;
    };

    const onDragAsset = event => {
        // if nothing's selected, return early
        const selections = getSelections();
        if (!selections.some(selection => selection)) {
            return;
        }

        event.dataTransfer.setDragImage(Util.getDragIcon(), 0, 0);
        setDraggingAsset(true);
        const moveList = getAssetList();
        setMovementSrc(moveList);
    };

    const getNodeFromSelectedNode = () => {
        const selectedNode = navtree?.selectedNode;
        if (selectedNode) {
            const pathArray =
                selectedNode.indexOf('>') !== -1 ? selectedNode.split('>') : [Util.ROOT_DIR, selectedNode];
            return Util.findNode(pathArray, navtree?.folderData);
        }
        return null;
    };

    const getSelectedPathForUploads = (refreshFolder = false) => {
        const selectedNode = getNodeFromSelectedNode();
        const rval = selectedNode ? selectedNode.path : null;
        if (refreshFolder) {
            loadAssets();
        }
        return rval;
    };

    const onDeleteAsset = async (assetObject, isMaintenance = false) => {
        if (!assetObject || !assetObject.id) {
            return false;
        }
        const assetId = assetObject.id;

        const result = await smartFetch(`${Util.WRITE_ROOT_PATH}/assets/${assetId}`, 'DELETE');
        if (!result || result.status === '200' || (!result?.message && result.assetId)) {
            if (!isMaintenance) {
                pageDispatch({type: 'UPDATE_ASSETS', command: 'DELETE', assets: [assetObject]});
                clearSelections();
            }
            return true;
        }
        const reason = result && result.message ? `because ${result.message}` : '';
        if (process.env.NODE_ENV !== 'test') {
            console.warn(`Unable to delete asset ${assetId} ${reason}`); // eslint-disable-line
        }
        return false;
    };

    const onDeleteAssetList = async (list, isMaintenance = false) => {
        const deleteRequests = [];
        list.forEach(assetObj => {
            deleteRequests.push(onDeleteAsset(assetObj, isMaintenance)); // Only refresh folder after all are done
        });
        const replies = await Promise.all(deleteRequests);
        if (!isMaintenance) {
            const successList = list.filter((_, idx) => replies[idx] === true);
            if (successList.length > 0) {
                pageDispatch({type: 'UPDATE_ASSETS', command: 'DELETE', assets: successList});
                clearSelections();
            }
        }
        return replies;
    };

    const doMaintenanceCleanup = async assetList => {
        pageDispatch({type: 'DELETE_MAINTENANCE_ASSETS'});
        console.log('Performing maintenance cleanup...', assetList); // eslint-disable-line no-console

        const BATCH_SIZE = 100; // So as not to overwhelm the backend with too many delete requests at once
        const successes = [];
        const failures = [];
        for (let i = 0; i < assetList.length; i += BATCH_SIZE) {
            const batch = assetList.slice(i, i + BATCH_SIZE);
            // eslint-disable-next-line no-await-in-loop
            const batchSuccesses = await onDeleteAssetList(batch, true);
            batchSuccesses.forEach((isSuccess, idx) => {
                if (isSuccess) {
                    successes.push(batch[idx]);
                } else {
                    failures.push(batch[idx]);
                }
            });
        }
        pageDispatch({type: 'COMPLETE_MAINTENANCE_CLEANUP', command: 'DELETE', assets: successes, failures}); // Refresh current view
    };

    const onMoveAsset = async (assetObject, parentId) => {
        if (!assetObject || !assetObject.id || !parentId) {
            return false;
        }

        const assetId = assetObject.id;
        const result = await smartFetch(`${Util.WRITE_ROOT_PATH}/assets/${assetId}/parent/${parentId}`, 'PATCH', '');
        if (!result || result.status === '200' || (!result?.message && result.movedAssets)) {
            return true;
        }
        const reason = result && result.message ? `because ${result.message}` : '';
        if (process.env.NODE_ENV !== 'test') {
            console.warn(`Unable to move asset ${assetId} ${reason}`); // eslint-disable-line
        }
        return false;
    };

    const onMoveAssetList = async (list, parentId) => {
        const moveRequests = [];
        list.forEach(assetObj => {
            moveRequests.push(onMoveAsset(assetObj, parentId));
        });
        const replies = await Promise.all(moveRequests);
        const successList = list.filter((_, idx) => replies[idx] === true);
        if (successList.length > 0) {
            pageDispatch({type: 'UPDATE_ASSETS', command: 'DELETE', assets: successList});
            clearSelections();
        }
        return replies;
    };

    const getShowModal = () => {
        return showModal;
    };

    const getShowGrid = () => {
        return showGrid;
    };

    const getSearchQuery = () => {
        return searchQuery;
    };

    const setSearchQuery = async query => {
        searchQuery = query || '';
        pageDispatch({type: 'SET_SEARCH_BY', searchBy: searchQuery});
        clearSelections();
        return query;
    };

    const getFilterSelect = () => {
        return filterSelect;
    };

    const setFilterSelect = async value => {
        if (filterSelect !== value) {
            if (filterSelect !== value) {
                filterSelect = value;
                pageDispatch({type: 'SET_FILTER_BY', filterBy: value});
                clearSelections();
                clearPages();
                return value;
            }
        }
        return null;
    };

    const onTagClick = idx => {
        if (tagSelected !== idx && idx >= 0 && idx < tags.length) {
            setTagSelected(idx);
            pageDispatch({type: 'SET_TAG', tag: getTagFromIndex(idx)});
            clearSelections();
            // currentPage[TAG_IDX] = 0;
            return true;
        }
        return false; // No data updated
    };

    // Handles case where Tags View sidebar is in view and we've added/deleted an asset tag
    // Seems to take a while for backend to update account tags list so try to reach eventual consistency
    //
    const tagsHaveChanged = () => {
        tagChanges = true; // This never gets reset (by design)
        if (typeIndex === TAG_IDX) {
            return new Promise(resolve => {
                setTimeout(
                    async () => {
                        await loadTagsList(getAccountId());
                        resolve(true);
                    },
                    Util.TESTING_MODE ? 0 : 8000
                );
            });
        }
        return Promise.resolve(true);
    };

    // Calls loadTagsList and immediately returns a resolved Promise to prevent an extra long wait spinner. Waits for list to load in the case of unit tests.
    //
    const ignoreLongSidebarRefreshWait = () => {
        const longWaitingPromise = tagsHaveChanged();
        return Util.TESTING_MODE ? longWaitingPromise : Promise.resolve(true);
    };

    const deleteAssetTagRequest = async (assetId, tag) => {
        const url = `${Util.WRITE_ROOT_PATH}/assets/${Util.safeURIencode(assetId)}/tag?value=${Util.safeURIencode(
            tag
        )}`;
        await smartFetch(url, 'DELETE'); // The backend doesn't return anything actionable
        return ignoreLongSidebarRefreshWait();
    };

    const onDeleteTag = async (idx, isIgnoreLongSidebarRefreshWait = true) => {
        if (focused && idx >= 0 && idx < assetTags.length) {
            const status = await deleteAssetTagRequest(focused.id, assetTags[idx], isIgnoreLongSidebarRefreshWait);
            const assetTagz = [...assetTags];
            assetTagz.splice(idx, 1);
            setAssetTags(assetTagz);
            return status;
        }
        return false;
    };

    const onAddTag = async tag => {
        if (!assetTags.includes(tag)) {
            const accountId = getAccountId();
            const assetId = focused?.id;
            if (assetId) {
                const url = `${Util.WRITE_ROOT_PATH}/assets/${Util.safeURIencode(assetId)}/tag`;
                await smartFetch(url, 'PATCH', JSON.stringify({tag, assetId, accountId})); // Returns nothing actionable
                const assetTagz = [...assetTags];
                assetTagz.push(tag);
                setAssetTags(assetTagz);
                return ignoreLongSidebarRefreshWait();
            }
        }
        return false;
    };

    const updateStatePrevAndNext = (prev, next) => {
        setPreviousEnabled(prev);
        setNextEnabled(next);
    };

    const onPreviousAsset = currentAssetIdx => {
        if (getShowModal()) {
            return false;
        }
        const focusedId = focused?.id;
        const assetIndex = Util.isValidAssetId(focusedId)
            ? assets.findIndex(asset => asset.id === focusedId)
            : currentAssetIdx; // currentAssetIdx is only guaranteed to be used by stock images
        const prevAssetIndex = assetIndex - 1;

        if (previousEnabled && assetIndex === 0) {
            updateStatePrevAndNext(false, false);
            setPage(page - 1);
            setFocusTo(pageSize - 1);
        } else {
            updateStatePrevAndNext(false, false);
            changeShowFocused(assets[prevAssetIndex], prevAssetIndex);
        }

        return true;
    };

    const onNextAsset = currentAssetIdx => {
        if (getShowModal()) {
            return false;
        }
        const focusedId = focused?.id;
        const assetIndex = Util.isValidAssetId(focusedId)
            ? assets.findIndex(asset => asset.id === focusedId)
            : currentAssetIdx; // currentAssetIdx is only guaranteed to be used by stock images
        const nextAssetIndex = assetIndex + 1;

        if (nextEnabled && assetIndex === assets.length - 1) {
            updateStatePrevAndNext(false, false);
            setPage(page + 1);
            setFocusTo(0);
        } else {
            updateStatePrevAndNext(false, false);
            changeShowFocused(assets[nextAssetIndex], nextAssetIndex);
        }

        return true;
    };

    const checkForPrevAndNext = (assetId, viewData, page_, maxPage_, assetIdx) => {
        const assetIndex = Util.isValidAssetId(assetId) ? viewData.findIndex(asset => asset.id === assetId) : assetIdx; // assetIdx is only guaranteed to be used by stock images

        const prev = page_ > 0 || assetIndex > 0;
        const next = page_ < maxPage_ - 1 || assetIndex < viewData.length - 1;
        return {prev, next};
    };

    // manually update viewData, presumably before a refresh from the server
    const updateViewData = asset => {
        if (asset && Util.isValidAssetId(asset.id)) {
            const viewData = getViewData();
            const idx = viewData.findIndex(oldAsset => oldAsset.id === asset.id);
            viewData[idx] = asset;
        }
    };

    useEffect(() => {
        const loadNav = async () => {
            await loadTopLevelData();
            await loadTagsList(getAccountId());
        };
        const isWidget = window.location.search?.includes('widget=true');
        if (isWidget) {
            const el = document.querySelector('body');
            if (el) {
                el.classList.add('widget');
            }
        }
        loadNav();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (error) {
            setContextError({context: `${typeIndex ? 'folder' : 'tag'} assets`, error});
            Util.loggedOutCheck();
        } else if (navtree.error) {
            setContextError({context: 'navtree', error: navtree.error});
            Util.loggedOutCheck();
        } else {
            setContextError(null);
        }
    }, [error, navtree.error, typeIndex]);

    useEffect(() => {
        if (focusTo !== null) {
            if (focusTo >= 0 && focusTo < assets.length) {
                changeShowFocused(assets[focusTo], focusTo);
            }
            setFocusTo(null);
        }
    }, [focusTo, assets, changeShowFocused]);

    if (page !== currentPage[typeIndex]) {
        currentPage[typeIndex] = page; // Sync currentPage with page from AssetsProvider
    }

    const controllerApi = {
        getShowModal,
        setShowModal,
        getAssetList,
        setShowFocused: changeShowFocused,
        getShowGrid,
        setShowGrid,
        getSearchQuery,
        setSearchQuery,
        getFilterSelect,
        setFilterSelect,
        getInternalSortBy,
        setInternalSortBy: changeInternalSortBy,
        getDateRange,
        setDateRange,
        getLocked,
        isAdvertisingRoot,
        isShowingTags,
        loadAssets,
        updateViewData,
        doMaintenanceCleanup
    };

    return (
        <MediaLibraryView
            onFolderAction={onFolderAction}
            onCreateFolder={onCreateFolder}
            onRenameSelectedFolder={onRenameSelectedFolder}
            onDeleteSelectedFolder={onDeleteSelectedFolder}
            contextError={contextError}
            loading={loading}
            tabIdx={typeIndex}
            viewData={loading ? null : assets}
            focused={focused}
            selections={getLocked() ? undefined : getSelections()}
            maxPages={maxPage + 1}
            page={page}
            pageSize={pageSize}
            size={assets?.length || 0}
            totalAssets={totalAssets}
            setPage={setPage}
            setPageSize={setPageSize}
            onSelect={onSelectChange}
            onTabSelect={onTabSelect}
            setMovementSrc={setMovementSrc}
            onDragAsset={onDragAsset}
            setDraggingAsset={setDraggingAsset}
            getSelectedPathForUploads={getSelectedPathForUploads}
            onDeleteAssetList={onDeleteAssetList}
            onMoveAssetList={onMoveAssetList}
            assetFilters={assetFilters}
            movementSrc={movementSrc}
            isDraggingAsset={isDraggingAsset}
            onAction={onAction}
            controllerApi={controllerApi}
            onEditAsset={onEditAsset}
            onTagClick={onTagClick}
            tags={tags}
            tagSelected={tagSelected}
            assetTags={assetTags}
            onDeleteTag={onDeleteTag}
            onAddTag={onAddTag}
            onPreviousAsset={onPreviousAsset}
            onNextAsset={onNextAsset}
            previousEnabled={previousEnabled}
            nextEnabled={nextEnabled}
            updateStatePrevAndNext={updateStatePrevAndNext}
            checkForPrevAndNext={checkForPrevAndNext}
            maintenance={Util.isMaintenanceMode() && getInternalUser() ? maintenance : null}
            pageDispatch={pageDispatch}
        />
    );
}
